/*
 * My Routine planner.
 * - Choices live in localStorage; the Shopify cart (/cart.js) is the source of truth for "In your bag".
 * - Product routine fields come from /products/<handle>?view=routine-data.
 * - Never removes cart items; never preselects paid extras.
 */
(() => {
  if (customElements.get('my-routine')) return;

  const STORAGE_KEY = 'naturall-my-routine-v1';
  const GROUP_TO_JOB = {
    'One cleanser': 'cleanse',
    'One rinse-out conditioner': 'condition',
    Moisturizer: 'moisturize',
    'Selected styler': 'style',
    'Optional oil': 'oil',
    'Optional edge finish': 'edge',
    'Optional tool': 'tool',
    Refresher: 'refresh',
    Set: 'set',
  };
  const productCache = new Map();

  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache.has(handle)) return productCache.get(handle);
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    // No JSON Accept header: Shopify would answer with its standard product JSON instead of this view.
    const promise = fetch(`${root}products/${encodeURIComponent(handle)}?view=routine-data`)
      .then((response) => (response.ok ? response.text() : null))
      .then((text) => {
        if (!text) return null;
        try {
          const data = JSON.parse(text);
          return data && data.handle && Array.isArray(data.variants) ? data : null;
        } catch (e) {
          return null;
        }
      })
      .catch(() => null);
    productCache.set(handle, promise);
    return promise;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[c]);
  }

  class MyRoutine extends HTMLElement {
    connectedCallback() {
      this.labels = this.dataset;
      this.toggleButton = this.querySelector('[data-routine-toggle]');
      this.panel = this.querySelector('.my-routine__panel');
      this.rowsEl = this.querySelector('[data-routine-rows]');
      this.submitButton = this.querySelector('[data-routine-submit]');
      this.messageEl = this.querySelector('[data-routine-message]');
      this.overlapEl = this.querySelector('[data-routine-overlap]');
      this.countEl = this.querySelector('[data-routine-count]');
      this.rowEls = Array.from(this.querySelectorAll('.my-routine__row'));
      this.jobs = this.rowEls.map((row) => row.dataset.job);
      this.state = this.loadState();
      this.cartItems = [];
      this.busy = false;

      document.documentElement.classList.add('has-my-routine');

      this.toggleButton.addEventListener('click', () => this.setOpen(this.panel.hidden));
      this.querySelector('[data-routine-close]').addEventListener('click', () => {
        this.setOpen(false);
        this.toggleButton.focus();
      });
      this.panel.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.setOpen(false);
          this.toggleButton.focus();
        }
      });
      this.rowsEl.addEventListener('click', this.onRowClick.bind(this));
      this.rowsEl.addEventListener('change', this.onRowChange.bind(this));
      this.overlapEl.addEventListener('click', this.onOverlapClick.bind(this));
      this.submitButton.addEventListener('click', this.addRoutine.bind(this));

      this.onCardClick = this.onCardClick.bind(this);
      document.addEventListener('click', this.onCardClick);

      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
        this.unsubscribe = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.source === 'my-routine') return;
          this.refreshCart();
        });
      }
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.refreshCart();
      });

      this.onApply = this.applySelections.bind(this);
      document.addEventListener('my-routine:apply', this.onApply);

      this.refreshCart();
    }

    /* Suggestions from the quiz: fill rows as "Selected to buy" or "Already have". Never adds to cart. */
    async applySelections(event) {
      const selections = (event.detail && event.detail.selections) || [];
      for (const sel of selections) {
        if (!this.jobs.includes(sel.job)) continue;
        if (sel.status === 'have') {
          this.state.rows[sel.job] = { status: 'have' };
          continue;
        }
        const data = await fetchProduct(sel.handle);
        if (!data) continue;
        const available = data.variants.filter((v) => v.available);
        this.state.rows[sel.job] = {
          handle: data.handle,
          variantId: data.variants.length === 1 && available.length === 1 ? data.variants[0].id : null,
          qty: 1,
          status: 'selected',
        };
      }
      this.saveState();
      await this.render();
      this.setOpen(true);
      const focusTarget = this.panel.querySelector('select, [data-action], button');
      if (focusTarget) focusTarget.focus();
    }

    disconnectedCallback() {
      document.removeEventListener('my-routine:apply', this.onApply);
      document.removeEventListener('click', this.onCardClick);
      if (this.unsubscribe) this.unsubscribe();
      document.documentElement.classList.remove('has-my-routine');
    }

    /* ---------- state ---------- */
    loadState() {
      try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
        if (saved && saved.rows) return saved;
      } catch (e) {
        /* ignore */
      }
      return { rows: {}, keepBoth: false };
    }

    saveState() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
      } catch (e) {
        /* storage unavailable: planner still works for this visit */
      }
    }

    row(job) {
      if (!this.state.rows[job]) this.state.rows[job] = {};
      return this.state.rows[job];
    }

    /* ---------- cart ---------- */
    async refreshCart() {
      try {
        const root = this.dataset.rootUrl.replace(/\/?$/, '/');
        const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await response.json();
        this.cartItems = cart.items || [];
      } catch (e) {
        this.cartItems = [];
      }
      await Promise.all(this.cartItems.map((item) => fetchProduct(item.handle)));
      await Promise.all(
        Object.values(this.state.rows)
          .filter((r) => r.handle)
          .map((r) => fetchProduct(r.handle))
      );
      this.render();
    }

    async bagByJob() {
      const map = {};
      for (const item of this.cartItems) {
        const data = await fetchProduct(item.handle);
        if (!data) continue;
        const job = GROUP_TO_JOB[data.group];
        if (!job) continue;
        (map[job] = map[job] || []).push({ item, data });
      }
      return map;
    }

    /* ---------- card button ---------- */
    async onCardClick(event) {
      const button = event.target.closest('[data-routine-add]');
      if (!button) return;
      event.preventDefault();
      const data = await fetchProduct(button.dataset.routineAdd);
      const job = data ? GROUP_TO_JOB[data.group] : null;
      if (!data || !job || !this.jobs.includes(job)) {
        // Never a dead click: fall back to the product page.
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        window.location.href = `${root}products/${encodeURIComponent(button.dataset.routineAdd)}`;
        return;
      }
      const row = this.row(job);
      row.handle = data.handle;
      row.variantId = data.variants.length === 1 ? data.variants[0].id : null;
      row.qty = 1;
      row.status = 'selected';
      if (job === 'set') this.state.keepBoth = false;
      this.saveState();
      await this.render();
      this.setOpen(true);
      const rowEl = this.rowEls.find((el) => el.dataset.job === job);
      if (rowEl) {
        const focusTarget = rowEl.querySelector('select') || rowEl.querySelector('a, button');
        if (focusTarget) focusTarget.focus();
      }
    }

    /* ---------- panel ---------- */
    setOpen(open) {
      this.panel.hidden = !open;
      this.toggleButton.setAttribute('aria-expanded', String(open));
      this.classList.toggle('is-open', open);
    }

    onRowClick(event) {
      const action = event.target.closest('[data-action]');
      if (!action) return;
      const job = action.closest('.my-routine__row').dataset.job;
      const row = this.row(job);
      switch (action.dataset.action) {
        case 'have':
          row.status = row.status === 'have' ? (row.handle ? 'selected' : null) : 'have';
          break;
        case 'skip':
          row.status = row.status === 'skip' ? (row.handle ? 'selected' : null) : 'skip';
          break;
        case 'remove':
          delete this.state.rows[job];
          break;
      }
      this.saveState();
      this.render();
    }

    onRowChange(event) {
      const rowEl = event.target.closest('.my-routine__row');
      if (!rowEl) return;
      const row = this.row(rowEl.dataset.job);
      if (event.target.matches('[data-variant]')) {
        row.variantId = event.target.value ? Number(event.target.value) : null;
      }
      if (event.target.matches('[data-qty]')) {
        const qty = Math.max(1, Math.min(10, parseInt(event.target.value, 10) || 1));
        row.qty = qty;
      }
      this.saveState();
      this.render();
    }

    onOverlapClick(event) {
      const button = event.target.closest('[data-overlap]');
      if (!button) return;
      if (button.dataset.overlap === 'replace') {
        (this.overlapJobs || []).forEach((job) => {
          const row = this.state.rows[job];
          if (row && row.status === 'selected') delete this.state.rows[job];
        });
        this.state.keepBoth = false;
      } else {
        this.state.keepBoth = true;
      }
      this.saveState();
      this.render();
    }

    /* ---------- computing ---------- */
    async compute() {
      const bag = await this.bagByJob();
      const rows = {};
      for (const job of this.jobs) {
        const saved = this.state.rows[job] || {};
        const data = saved.handle ? await fetchProduct(saved.handle) : null;
        rows[job] = { job, saved, data, bag: bag[job] || [] };
      }

      // Sets: selected set or sets in the bag cover their components' jobs.
      const covered = {};
      const setSources = [];
      const setRow = rows.set;
      if (setRow && setRow.data && setRow.saved.status === 'selected') {
        setSources.push({ data: setRow.data, inBag: false });
      }
      (bag.set || []).forEach(({ data }) => setSources.push({ data, inBag: true }));
      setSources.forEach(({ data, inBag }) => {
        (data.components || []).forEach((component) => {
          const job = GROUP_TO_JOB[component.group];
          if (job && !covered[job]) covered[job] = { set: data, inBag };
        });
      });

      // Moisturizer with a Refresh secondary role covers Between Washes.
      const moist = rows.moisturize;
      const moistProduct =
        (moist && moist.saved.status === 'selected' && moist.data) || (moist && moist.bag[0] && moist.bag[0].data);
      if (moistProduct && (moistProduct.secondary || []).includes('Refresh') && !covered.refresh) {
        covered.refresh = { product: moistProduct };
      }

      const overlapJobs = [];
      if (setSources.length) {
        Object.keys(covered).forEach((job) => {
          if (!covered[job].set) return;
          const r = rows[job];
          if (r && r.saved.status === 'selected' && r.data) overlapJobs.push(job);
        });
      }

      let total = 0;
      const lines = [];
      let coveredCount = 0;
      let requiredCount = 0;

      for (const job of this.jobs) {
        const r = rows[job];
        const rowEl = this.rowEls.find((el) => el.dataset.job === job);
        const optional = rowEl.dataset.optional === 'true';
        const status = r.saved.status;
        const inBagSame = r.data && r.bag.some((b) => b.data.handle === r.data.handle);
        const cover = covered[job];
        const coveredBySetOnly = cover && cover.set && !this.state.keepBoth;
        let variant = null;
        if (r.data) {
          if (r.saved.variantId) variant = r.data.variants.find((v) => v.id === r.saved.variantId) || null;
          else if (r.data.variants.length === 1) variant = r.data.variants[0];
        }

        let state = optional ? 'optional' : 'not_chosen';
        if (status === 'have') state = 'have';
        else if (status === 'skip') state = 'skip';
        else if (r.data && status === 'selected' && !inBagSame && !(coveredBySetOnly && job !== 'set')) state = 'selected';
        else if (r.bag.length) state = 'in_bag';
        else if (cover) state = 'covered';

        r.state = state;
        r.variant = variant;
        r.cover = cover;
        r.inBagSame = inBagSame;

        if (state === 'selected' && variant && variant.available && !r.data.requiresSellingPlan) {
          const qty = r.saved.qty || 1;
          total += variant.price * qty;
          lines.push({ id: variant.id, quantity: qty, title: r.data.title, job });
        }

        if (!optional) {
          requiredCount += 1;
          if (['have', 'in_bag', 'covered'].includes(state) || (state === 'selected' && variant)) coveredCount += 1;
        }
      }

      // Set chosen here whose included products are already in the bag (exact product match).
      const bagDupes = [];
      if (setRow && setRow.data && setRow.saved.status === 'selected' && !setRow.bag.some((b) => b.data.handle === setRow.data.handle)) {
        const bagHandles = new Set((this.cartItems || []).map((item) => item.handle));
        (setRow.data.components || []).forEach((component) => {
          if (bagHandles.has(component.handle)) bagDupes.push(component.title);
        });
      }

      return { rows, total, lines, coveredCount, requiredCount, overlapJobs, bagDupes };
    }

    formatMoney(cents) {
      const currency = this.dataset.currency || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency,
          minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
          maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
        }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2);
      }
    }

    /* ---------- rendering ---------- */
    async render() {
      const active = document.activeElement;
      const activeId = active && this.contains(active) && active.id ? active.id : null;
      const result = await this.compute();
      this.result = result;
      const L = this.labels;

      for (const rowEl of this.rowEls) {
        const r = result.rows[rowEl.dataset.job];
        const label = rowEl.dataset.label;
        const statusText = {
          not_chosen: L.labelNotChosen,
          optional: L.labelOptional,
          selected: L.labelSelected,
          in_bag: L.labelInBag,
          have: L.labelHave,
          skip: L.labelSkip,
          covered: L.labelCovered,
        }[r.state];
        const statusEl = rowEl.querySelector('[data-status]');
        statusEl.textContent = statusText || '';
        statusEl.dataset.state = r.state;
        rowEl.dataset.state = r.state;

        let html = '';
        if (r.data && (r.saved.status === 'selected' || r.saved.status === 'have' || r.saved.status === 'skip')) {
          const d = r.data;
          const options = d.variants
            .map(
              (v) =>
                `<option value="${v.id}" ${r.saved.variantId === v.id ? 'selected' : ''} ${v.available ? '' : 'disabled'}>${escapeHtml(
                  v.title
                )}${v.available ? '' : ` (${escapeHtml(L.labelSoldOut)})`}</option>`
            )
            .join('');
          const variantControl =
            d.variants.length > 1
              ? `<label class="visually-hidden" for="rv-${rowEl.dataset.job}-${this.id}">${escapeHtml(d.title)}</label>
                 <select id="rv-${rowEl.dataset.job}-${this.id}" class="my-routine__select" data-variant>
                   <option value="">${escapeHtml(L.labelChooseOption)}</option>${options}
                 </select>`
              : '';
          const price = r.variant ? this.formatMoney(r.variant.price) : '';
          const soldOut = r.variant && !r.variant.available;
          html += `<div class="my-routine__pick">
              ${d.image ? `<img src="${escapeHtml(d.image)}" alt="" width="48" height="48" loading="lazy">` : ''}
              <div class="my-routine__pick-info">
                <a href="${escapeHtml(d.url)}" class="my-routine__pick-title">${escapeHtml(d.title)}</a>
                ${variantControl}
                <div class="my-routine__pick-meta">
                  ${
                    r.state === 'selected'
                      ? `<label class="visually-hidden" for="rq-${rowEl.dataset.job}-${this.id}">${escapeHtml(L.labelQuantity)}</label>
                         <input id="rq-${rowEl.dataset.job}-${this.id}" class="my-routine__qty" type="number" min="1" max="10" value="${
                           r.saved.qty || 1
                         }" data-qty>`
                      : ''
                  }
                  ${price ? `<span class="my-routine__price">${price}</span>` : ''}
                  ${soldOut ? `<span class="my-routine__warn">${escapeHtml(L.labelSoldOut)}</span>` : ''}
                </div>
              </div>
            </div>`;
        }
        if (r.bag.length) {
          html += `<p class="my-routine__line">${escapeHtml(L.labelInBag)}: ${r.bag
            .map((b) => escapeHtml(b.item.product_title))
            .join(', ')}</p>`;
        }
        if (r.cover && (r.state === 'covered' || r.state === 'not_chosen' || r.state === 'optional')) {
          const by = r.cover.set ? r.cover.set.title : r.cover.product.title;
          html += `<p class="my-routine__line">${escapeHtml(L.labelCovered)} ${escapeHtml(by)}</p>`;
        }
        if (!r.data && !r.bag.length && !r.cover && rowEl.dataset.url) {
          html += `<a class="my-routine__browse link" href="${escapeHtml(rowEl.dataset.url)}">${escapeHtml(
            L.labelBrowse
          )} ${escapeHtml(label)}</a>`;
        }
        const pressedHave = r.saved.status === 'have';
        const pressedSkip = r.saved.status === 'skip';
        html += `<div class="my-routine__actions">
            <button type="button" class="my-routine__chip" data-action="have" aria-pressed="${pressedHave}">${escapeHtml(
              L.labelHave
            )}</button>
            <button type="button" class="my-routine__chip" data-action="skip" aria-pressed="${pressedSkip}">${escapeHtml(
              L.labelSkip
            )}</button>
            ${
              r.data
                ? `<button type="button" class="my-routine__chip my-routine__chip--remove" data-action="remove">${escapeHtml(
                    L.labelRemove
                  )}</button>`
                : ''
            }
          </div>`;
        rowEl.querySelector('[data-body]').innerHTML = html;
      }

      // Overlap between a set and individual bottles.
      this.overlapJobs = result.overlapJobs;
      const bagDupes = result.bagDupes || [];
      const bagNote = bagDupes.length
        ? `<p class="my-routine__bag-note">${escapeHtml(L.labelBagOverlap)} <span>${bagDupes.map(escapeHtml).join(', ')}</span></p>`
        : '';
      if (result.overlapJobs.length && !this.state.keepBoth) {
        const names = result.overlapJobs.map((job) => result.rows[job].data.title);
        this.overlapEl.innerHTML = `${bagNote}<p>${escapeHtml(L.labelOverlap)} <span>${names
          .map(escapeHtml)
          .join(', ')}</span></p>
          <div class="my-routine__actions">
            <button type="button" class="button button--secondary button--small" data-overlap="replace">${escapeHtml(
              L.labelReplace
            )}</button>
            <button type="button" class="button button--tertiary button--small" data-overlap="keep">${escapeHtml(
              L.labelKeep
            )}</button>
          </div>`;
        this.overlapEl.hidden = false;
      } else if (bagNote) {
        this.overlapEl.innerHTML = bagNote;
        this.overlapEl.hidden = false;
      } else {
        this.overlapEl.hidden = true;
        this.overlapEl.innerHTML = '';
      }

      const countText = (L.labelCoveredCount || '')
        .replace('[covered]', result.coveredCount)
        .replace('[total]', result.requiredCount);
      this.countEl.textContent = countText;

      if (!this.busy) {
        this.submitButton.disabled = result.lines.length === 0;
        this.submitButton.textContent = result.lines.length
          ? `${L.labelButton} — ${this.formatMoney(result.total)}`
          : L.labelButton;
      }

      if (activeId) {
        const again = document.getElementById(activeId);
        if (again && again !== document.activeElement) again.focus();
      }

      // Reflect selection on product cards.
      const selectedHandles = new Set(
        Object.values(this.state.rows)
          .filter((r) => r.handle && r.status === 'selected')
          .map((r) => r.handle)
      );
      document.querySelectorAll('[data-routine-add]').forEach((button) => {
        const selected = selectedHandles.has(button.dataset.routineAdd);
        button.classList.toggle('is-selected', selected);
        const label = button.querySelector('.routine-add__label');
        const done = button.querySelector('.routine-add__done');
        if (label && done) {
          label.hidden = selected;
          done.hidden = !selected;
        }
      });
    }

    /* ---------- add to cart ---------- */
    async addRoutine() {
      if (this.busy) return;
      const result = await this.compute();
      if (!result.lines.length) {
        this.messageEl.textContent = this.labels.labelNothing;
        return;
      }
      this.busy = true;
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-busy', 'true');
      this.submitButton.textContent = this.labels.labelAdding;
      this.messageEl.textContent = '';

      const root = this.dataset.rootUrl.replace(/\/?$/, '/');
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer ? drawer.getSectionsToRender().map((s) => s.id) : [];
      const failed = [];
      let sectionsPayload = null;

      const post = (items, withSections) =>
        fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(
            withSections && sections.length
              ? { items, sections: sections.join(','), sections_url: window.location.pathname }
              : { items }
          ),
        });

      try {
        const response = await post(
          result.lines.map(({ id, quantity }) => ({ id, quantity })),
          true
        );
        if (response.ok) {
          const json = await response.json();
          sectionsPayload = json.sections || null;
        } else {
          // Add one by one so we can report exactly which product couldn't be added.
          for (const line of result.lines) {
            const single = await post([{ id: line.id, quantity: line.quantity }], false);
            if (!single.ok) {
              let reason = '';
              try {
                const err = await single.json();
                reason = err.description || err.message || '';
              } catch (e) {
                /* ignore */
              }
              failed.push(`${line.title}${reason ? ` (${reason})` : ''}`);
            }
          }
        }
      } catch (e) {
        failed.push(...result.lines.map((l) => l.title));
      }

      if (!sectionsPayload && sections.length) {
        try {
          const response = await fetch(`${window.location.pathname}?sections=${sections.join(',')}`);
          sectionsPayload = await response.json();
        } catch (e) {
          sectionsPayload = null;
        }
      }

      this.busy = false;
      this.submitButton.removeAttribute('aria-busy');
      await this.refreshCart();

      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'my-routine' });
      }

      if (failed.length) {
        this.messageEl.textContent = `${this.labels.labelFailed} ${failed.join(', ')}`;
      }

      const added = result.lines.length - failed.length;
      if (added > 0) {
        if (drawer && sectionsPayload) {
          drawer.classList.remove('is-empty');
          drawer.renderContents({ sections: sectionsPayload });
          setTimeout(() => {
            const note = drawer.querySelector('[data-routine-added]');
            if (note) note.hidden = false;
          }, 50);
          if (!failed.length) this.setOpen(false);
        } else if (!drawer) {
          window.location.href = this.dataset.cartUrl;
        }
      }
    }
  }

  customElements.define('my-routine', MyRoutine);
})();
